import React, { useState, useEffect, useRef, useMemo } from "react";
import * as XLSX from "xlsx";
import {
  Baby, Info, Crown
} from "lucide-react";
import { storage } from "../firebase";
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { useModal } from "../contexts/ModalContext";
import { matchSearch } from "../utils/chosung";
import VisitLogModal from "../components/VisitLogModal";
import { isTeacherMember } from "../utils/roleUtils";
import StudentStatsSection from "./student/StudentStatsSection";
import StudentBulkRegistrationModal from "./student/StudentBulkRegistrationModal";
import StudentDetailModal from "./student/StudentDetailModal";
import StudentEditModal from "./student/StudentEditModal";
import StudentListContentSection from "./student/StudentListContentSection";
import StudentListHeaderToolbar from "./student/StudentListHeaderToolbar";
import { DEPARTMENTS, FAITH_STATE_STYLES, ICONS } from "./student/constants";
import GlassCard from "./student/GlassCard";
import useDraggableModal from "./student/useDraggableModal";
import { STUDENT_LIST_HEADER_TITLE_ROW_CLASS } from "./student/studentListHeaderStyles";
import { getDepartmentClassLabel } from "../utils/studentDisplay";
import {
  buildSortedStudents,
  buildStudentStats,
  getDefaultDepartmentValue,
} from "./student/studentListUtils";
import { enrichRecord } from "../utils/recordUtils";

export default function StudentListView({
  students = [],
  onAdd,
  onUpdate,
  onDelete,
  studentToEdit,
  onClearStudentToEdit,
  attendanceLogs = [],
  visitLogs = [],
  onUpdateLog,
  onDeleteLog,
  onBulkDelete,
  demoAction, // [NEW] Tour Demo Trigger
  onTourAction, // [NEW] Tour Advancement Trigger
  // [NEW] Unified Mobile Props
  listType = 'student', // 'student' or 'teacher'
  selectedDepartment,
  myDepartments = [],
  userProfile, // [FIX] Added userProfile
  theme,
  onIncrementStat, // [NEW] For achievement tracking
  targetStudentId = null,
  onClearTargetStudentId,
  detailReturnMenu = null,
  onClearDetailReturnMenu,
  onNavigateMenu
}) {
  const safeStudents = Array.isArray(students) ? students : [];

  // [NEW] Merge departments: User Configured ONLY. Fallback to defaults only if NOTHING is configured.
  const allDepartments = useMemo(() => {
    let finalList = [];

    // 1. User's Primary Department (Highest Priority)
    if (userProfile?.deptName) {
      finalList.push(userProfile.deptName);
    }

    // 2. User's Custom Departments
    if (Array.isArray(myDepartments) && myDepartments.length > 0) {
      finalList.push(...myDepartments);
    }

    // 3. Fallback: Only use default DEPARTMENTS if user has NO department info at all
    if (finalList.length === 0) {
      finalList = DEPARTMENTS;
    }

    // 4. Normalize & Deduplicate (NFC Safe)
    const uniqueSet = new Set();
    const result = [];

    finalList.forEach(dept => {
      const normalized = String(dept || "").normalize('NFC').trim();
      const isInvalid = !normalized || normalized === '기타' || normalized === 'custom' || normalized === 'undefined' || normalized === '직접 입력';

      if (!isInvalid && !uniqueSet.has(normalized)) {
        uniqueSet.add(normalized);
        result.push(dept);
      }
    });

    return result;
  }, [myDepartments, userProfile]);

  const [viewingLog, setViewingLog] = useState(null);
  // 검색 및 필터
  const [searchTerm, setSearchTerm] = useState("");
  const [filterDept, setFilterDept] = useState("전체");
  const [selectedIds, setSelectedIds] = useState([]);
  const [sortKey, setSortKey] = useState("name"); // [NEW] 정렬 키
  const [sortOrder, setSortOrder] = useState("asc"); // [NEW] 정렬 순서
  const [isDownloadMenuOpen, setIsDownloadMenuOpen] = useState(false); // [NEW] 다운로드 메뉴 상태
  const [viewMode, setViewMode] = useState("list");
  const { openConfirm, openAlert } = useModal();

  // [NEW] Permission check - teachers have limited access
  const isTeacher = isTeacherMember(userProfile);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const {
    pos: modalPos,
    setPos: setModalPos,
    handleMouseDown,
  } = useDraggableModal({
    blockedSelectors: ["button", "input", "select", "textarea"],
  });

  const {
    pos: detailModalPos,
    setPos: setDetailModalPos,
    handleMouseDown: handleDetailMouseDown,
  } = useDraggableModal({
    blockedSelectors: ["button", "a"],
  });

  const resolveTargetStudent = () => {
    if (typeof targetStudentId === 'string') {
      return safeStudents.find((student) => student.id === targetStudentId) || null;
    }
    if (targetStudentId?.kind === 'studentName' && targetStudentId.value) {
      return safeStudents.find((student) => student.name === targetStudentId.value) || null;
    }
    return null;
  };

  const [editingStudent, setEditingStudent] = useState(null);
  const [viewingStudent, setViewingStudent] = useState(() => {
    if (!targetStudentId) return null;
    return resolveTargetStudent();
  });
  const [isDeepLinkedDetailOpen, setIsDeepLinkedDetailOpen] = useState(Boolean(targetStudentId));
  const [isUploading, setIsUploading] = useState(false);


  const [isRegisterMenuOpen, setIsRegisterMenuOpen] = useState(false);
  const registerMenuRef = useRef(null);


  const [isBulkModalOpen, setIsBulkModalOpen] = useState(false);

  // [FIX] 최근 출석/심방 기록 실시간 동기화
  useEffect(() => {
    if (viewingStudent) {
      const refreshed = safeStudents.find(s => s.id === viewingStudent.id);
      if (refreshed) {
        setViewingStudent(enrichRecord(refreshed, attendanceLogs, visitLogs));
      }
    }
  }, [attendanceLogs, visitLogs, safeStudents]);




  // [FIX] 기본 부서 결정 로직: 부서 1개 → 자동 설정, 2개 이상 → 헤더 선택 또는 첫 번째 부서
  const getDefaultDepartment = () =>
    getDefaultDepartmentValue({
      allDepartments,
      selectedDepartment,
      userDeptName: userProfile?.deptName,
    });

  const [formData, setFormData] = useState({
    name: "", gender: "남", phone: "", grade: "", class: "", school: "",
    guardian1Name: "", guardian1Phone: "", guardian2Name: "", guardian2Phone: "",
    birth: "", address: "", faithState: "기존신자",
    notes: "", photoUrl: ""
  });

  // [NEW] Auto-calculate Grade based on Birthdate (Process Simplification)
  useEffect(() => {
    if (!formData.birth || formData.birth.length < 4 || editingStudent) return;

    // Assume YYYY-MM-DD or YYYY.MM.DD
    const matched = formData.birth.match(/^(\d{4})/);
    if (!matched) return;

    const birthYear = parseInt(matched[1]);
    const currentYear = new Date().getFullYear();
    const age = currentYear - birthYear + 1; // Korean Age

    let suggestedGrade = "";
    if (age >= 20) suggestedGrade = "청년부";
    else if (age >= 17) suggestedGrade = "고등부";
    else if (age >= 14) suggestedGrade = "중등부";
    else if (age >= 11) suggestedGrade = "초등부";
    else if (age >= 8) suggestedGrade = "유년부";
    else if (age >= 6) suggestedGrade = "유치부";
    else suggestedGrade = "영아부";

    if (suggestedGrade && formData.grade !== suggestedGrade) {
      setFormData(prev => ({ ...prev, grade: suggestedGrade }));
    }
  }, [formData.birth, editingStudent]);

  useEffect(() => {
    if (studentToEdit) {
      setEditingStudent(studentToEdit);
      setFormData(studentToEdit);
      setIsModalOpen(true);
      onClearStudentToEdit();
    }
  }, [studentToEdit, onClearStudentToEdit]);

  useEffect(() => {
    if (!targetStudentId) return;

    const targetStudent = resolveTargetStudent();
    if (!targetStudent) return;

    setViewingStudent(targetStudent);
    setIsDeepLinkedDetailOpen(true);
    if (onClearTargetStudentId) {
      onClearTargetStudentId();
    }
  }, [targetStudentId, safeStudents, onClearTargetStudentId]);

  const closeViewingStudent = () => {
    setViewingStudent(null);
    if (isDeepLinkedDetailOpen && detailReturnMenu && onNavigateMenu) {
      onNavigateMenu(detailReturnMenu);
      if (onClearDetailReturnMenu) onClearDetailReturnMenu();
    }
    setIsDeepLinkedDetailOpen(false);
  };

  // [NEW] Demo Action Handler
  useEffect(() => {
    if (demoAction === 'add' || demoAction === 'demo_add_student') {
      const demoData = {
        name: "김새신", gender: "남", phone: "010-1234-5678", grade: getDefaultDepartment(), // [FIX] Use user's default department
        school: "서울대학교", birth: "2000-01-01", address: "서울시 강남구",
        faithState: "초신자", notes: "친구 초대로 방문함"
      };

      const t1 = setTimeout(() => {
        setIsModalOpen(true);
        setEditingStudent(null);
        // [MODIFIED] Tour: Pre-fill data
        setFormData(demoData);
      }, 500);
      return () => clearTimeout(t1);
    } else if (demoAction === 'demo_save_student') {
      if (isModalOpen && formData.name) {
        onAdd(formData);
        setIsModalOpen(false);
      }
    }
  }, [demoAction]);

  // [NEW] Initialize form data when viewing student changes
  useEffect(() => {
    if (viewingStudent) {
      setDetailModalPos({ x: 0, y: 0 }); // Reset position

      // Pre-fill form data for potential editing
      setFormData({
        ...viewingStudent, // Spread existing properties
        // Ensure defaults for missing fields
        phone: viewingStudent.phone || "",
        grade: viewingStudent.grade || DEPARTMENTS[0],
        class: viewingStudent.class || "",
        birth: viewingStudent.birth || "",
        school: viewingStudent.school || "",
        address: viewingStudent.address || "",
        notes: viewingStudent.notes || "",
        faithState: viewingStudent.faithState || "기존신자",
        photoUrl: viewingStudent.photoUrl || ""
      });

      // [NEW] 생일 확인 업적 업데이트
      if (onIncrementStat && viewingStudent.birth) {
        const today = new Date();
        const birthStr = typeof viewingStudent.birth === 'string' ? viewingStudent.birth :
          (viewingStudent.birth?.toDate ? viewingStudent.birth.toDate().toISOString().split('T')[0] : String(viewingStudent.birth));
        const birthParts = birthStr.replace(/\./g, '-').split('-');
        if (birthParts.length >= 3) {
          const bMonth = parseInt(birthParts[1]);
          const bDay = parseInt(birthParts[2]);
          if (today.getMonth() + 1 === bMonth) {
            onIncrementStat('birthday_view_count', 1);
          }
        }
      }
    }
  }, [viewingStudent, onIncrementStat]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (registerMenuRef.current && !registerMenuRef.current.contains(event.target)) {
        setIsRegisterMenuOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  // ESC 키 핸들러
  useEffect(() => {
    const handleEsc = (event) => {
      if (event.key === "Escape") {
        if (isModalOpen) {
          // [NEW] 작성 중인 내용 확인
          const isDirty = formData.name || formData.phone || formData.school || formData.address || formData.notes;
          if (isDirty) {
            openConfirm("작성 취소", "작성 중인 내용이 있습니다. 정말 닫으시겠습니까?", () => {
              setIsModalOpen(false);
            });
          } else {
            setIsModalOpen(false);
          }
          return;
        }
        if (isBulkModalOpen) return setIsBulkModalOpen(false);
        if (viewingStudent) return closeViewingStudent();
      }
    };
    window.addEventListener("keydown", handleEsc);
    return () => window.removeEventListener("keydown", handleEsc);
  }, [isModalOpen, isBulkModalOpen, viewingStudent, formData, openConfirm, closeViewingStudent]);

  const sortedStudents = useMemo(() => {
    return buildSortedStudents({
      students: safeStudents,
      searchTerm,
      sortKey,
      sortOrder,
      selectedDepartment,
      visitLogs,
      attendanceLogs,
      matchSearch,
    });
  }, [safeStudents, searchTerm, sortKey, sortOrder, selectedDepartment, visitLogs, attendanceLogs]);

  // [통계 계산] - newStudentsCount, avgAttendance
  const { newStudentsCount, avgAttendance } = useMemo(() => {
    return buildStudentStats({
      students: safeStudents,
      attendanceLogs,
    });
  }, [safeStudents, attendanceLogs]);

  const openAddModal = () => {
    setEditingStudent(null);
    // [FIX] 기본 부서 결정: 부서 1개 → 자동 설정, 2개 이상 → 헤더 선택 부서
    const defaultDept = getDefaultDepartment();
    setFormData({
      name: "", gender: "남", phone: "", grade: defaultDept, class: "", school: "",
      guardian1Name: "", guardian1Phone: "", guardian2Name: "", guardian2Phone: "",
      birth: "", address: "", faithState: "기존신자",
      notes: "", photoUrl: ""
    });
    setModalPos({ x: 0, y: 0 });
    setIsModalOpen(true);
  };

  const openEditModal = (student) => {
    setEditingStudent(student);
    setFormData(student);
    setModalPos({ x: 0, y: 0 });
    setIsModalOpen(true);
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    setIsUploading(true);
    try {
      const storageRef = ref(storage, `students/${Date.now()}_${file.name}`);
      await uploadBytes(storageRef, file);
      const url = await getDownloadURL(storageRef);
      setFormData((prev) => ({ ...prev, photoUrl: url }));
    } catch (error) {
      console.error("Upload failed", error);
      openAlert("이미지 업로드 실패", "오류");
    } finally {
      setIsUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (editingStudent) {
      const updatedStudent = { ...formData, id: editingStudent.id };
      onUpdate(updatedStudent);

      // [FIX] 현재 보고 있는 학생이라면 viewingStudent 즉시 업데이트
      if (viewingStudent && viewingStudent.id === editingStudent.id) {
        setViewingStudent(prev => ({ ...prev, ...updatedStudent }));
      }
    } else {
      onAdd(formData);
      // [NEW] Trigger Tour Advance if in demo mode
      if (demoAction === 'demo_add_student' && onTourAction) {
        onTourAction();
      }
    }
    setIsModalOpen(false);
  };

  const handleDelete = (id) => {
    openConfirm(
      "기록을 정리할까요?",
      "삭제된 정보는 아쉽게도 복구할 수 없습니다. 신중히 결정해 주세요! ✨",
      () => {
        onDelete(id);
        setViewingStudent(null);
      }
    );
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) setSelectedIds(sortedStudents.map(s => s.id));
    else setSelectedIds([]);
  };

  const handleSelectRow = (id) => {
    if (selectedIds.includes(id)) setSelectedIds(selectedIds.filter(sid => sid !== id));
    else setSelectedIds([...selectedIds, id]);
  };

  const handleBulkDelete = () => {
    const namesToDelete = students.filter(s => selectedIds.includes(s.id)).map(s => s.name);
    openConfirm(
      "일괄 삭제",
      `${selectedIds.length}명을 삭제하시겠습니까?`,
      async () => {
        if (onBulkDelete) {
          await onBulkDelete(selectedIds, namesToDelete);
        } else {
          selectedIds.forEach(id => onDelete(id));
        }
        setSelectedIds([]);
      }
    );
  };

  const handleDownloadExcel = () => {
    // [MODIFIED] 선택된 항목만 다운로드
    if (selectedIds.length === 0) {
      openAlert("다운로드할 학생을 선택해주세요.", "알림");
      return;
    }

    openConfirm("명단 다운로드", `선택한 ${selectedIds.length}명의 명단을 다운로드하시겠습니까?`, () => {
      const selectedStudents = sortedStudents.filter(s => selectedIds.includes(s.id));
      const data = selectedStudents.map(s => ({
        이름: s.name,
        성별: s.gender,
        연락처: s.phone,
        '보호자1 관계': s.guardian1Name || '',
        '보호자1 연락처': s.guardian1Phone || '',
        '보호자2 관계': s.guardian2Name || '',
        '보호자2 연락처': s.guardian2Phone || '',
        부서: s.department || s.grade,
        반: s.class,
        학교: s.school,
        생년월일: s.birth,
        주소: s.address,
        신앙상태: s.faithState,
        비고: s.notes
      }));

      const ws = XLSX.utils.json_to_sheet(data);

      ws['!cols'] = [
        { wch: 10 }, { wch: 6 }, { wch: 15 }, { wch: 10 }, { wch: 15 }, { wch: 10 }, { wch: 15 },
        { wch: 10 }, { wch: 8 }, { wch: 15 }, { wch: 12 }, { wch: 30 }, { wch: 10 }, { wch: 30 }
      ];

      const wb = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(wb, ws, "학생명단");
      XLSX.writeFile(wb, `학생명단_선택${selectedIds.length}명_${new Date().toISOString().slice(0, 10)}.xlsx`);
    });
  };

  const handleCopyStats = () => {
    // [MODIFIED] 선택된 항목 상세 복사
    if (selectedIds.length === 0) {
      openAlert("복사할 학생을 선택해주세요.", "알림");
      return;
    }

    openConfirm("명단 복사", `선택한 ${selectedIds.length}명의 정보를 복사하시겠습니까?`, () => {
      const selectedStudents = sortedStudents.filter(s => selectedIds.includes(s.id));

      let text = `[📅 학생 명단] 총 ${selectedStudents.length}명 선택\n\n`;
      selectedStudents.forEach((s, index) => {
        const affiliation = getDepartmentClassLabel(s) || "-";
        text += `${index + 1}. ${s.name}\n`;
        text += `   - 성별: ${s.gender || '-'}\n`;
        text += `   - 연락처: ${s.phone || '-'}\n`;
        text += `   - 소속: ${affiliation}\n`;
        text += `   - 학교: ${s.school || '-'}\n`;
        text += `   - 생년월일: ${s.birth || '-'}\n`;
        text += `   - 주소: ${s.address || '-'}\n`;
        text += `   - 신앙상태: ${s.faithState || '-'}\n`;
        if (s.notes) text += `   - 비고: ${s.notes}\n`;
        text += `\n`;
      });
      text += `----------------\n(기준일: ${new Date().toLocaleDateString()})`;

      navigator.clipboard.writeText(text)
        .then(() => openAlert("명단이 복사되었습니다! 📋", "알림"))
        .catch(() => openAlert("복사 실패", "오류"));
    });
  };

  const handleShareClick = (student) => {
    const affiliation = getDepartmentClassLabel(student) || "-";
    const shareText = `[학생 정보]
이름: ${student.name} (${student.gender || "-"})
직분/부서: ${affiliation}
연락처: ${student.phone}
생년월일: ${student.birth || "-"}
학교/직장: ${student.school || "-"}
주소: ${student.address || "-"}
신앙상태: ${student.faithState}
최근심방: ${student.lastVisit || "-"}
최근출석: ${student.lastAttendance || "-"}

[비고]
${student.notes || "특이사항 없음"}
`;

    openConfirm("텍스트 복사", "학생 정보를 클립보드에 복사하시겠습니까?", () => {
      navigator.clipboard.writeText(shareText);
      openAlert("복사되었습니다!", "알림");
    });
  };

  // --- 일괄 등록 로직 ---

  // [NEW] Mobile Data Filtering


  // --- Start of Desktop Render ---

  return (
    <div className="w-full h-full flex flex-col min-h-0 overflow-hidden">
      <div className="flex-1 overflow-y-auto pb-20 pt-2 px-4 custom-scrollbar">
        <div className="max-w-7xl mx-auto w-full space-y-6">
          <div className={STUDENT_LIST_HEADER_TITLE_ROW_CLASS}>
            <div>
              <h2 className="text-3xl font-black text-[#2C3E30] flex items-center gap-3 font-do-hyeon">
                <img src={ICONS.student} width={48} alt="icon" className="drop-shadow-md" /> 학생 명단
              </h2>
              <p className="text-gray-500 text-sm font-medium mt-1 ml-1">
                총 <span className="font-bold text-[#354F38]">{sortedStudents.length}</span>명의 학생을 체계적으로 관리하세요.
              </p>
            </div>
          </div>

          <StudentStatsSection
            GlassCard={GlassCard}
            icons={ICONS}
            sortedStudentsLength={sortedStudents.length}
            newStudentsCount={newStudentsCount}
            avgAttendance={avgAttendance}
          />

          <StudentListHeaderToolbar
            icons={ICONS}
            sortedStudentsLength={sortedStudents.length}
            theme={theme}
            searchTerm={searchTerm}
            setSearchTerm={setSearchTerm}
            selectedDepartment={selectedDepartment}
            filterDept={filterDept}
            setFilterDept={setFilterDept}
            userProfile={userProfile}
            myDepartments={myDepartments}
            sortKey={sortKey}
            setSortKey={setSortKey}
            sortOrder={sortOrder}
            setSortOrder={setSortOrder}
            selectedIdsLength={selectedIds.length}
            handleSelectAllToggle={() => handleSelectAll({ target: { checked: selectedIds.length !== sortedStudents.length } })}
            viewMode={viewMode}
            setViewMode={setViewMode}
            isTeacher={isTeacher}
            handleBulkDelete={handleBulkDelete}
            isDownloadMenuOpen={isDownloadMenuOpen}
            setIsDownloadMenuOpen={setIsDownloadMenuOpen}
            handleDownloadExcel={handleDownloadExcel}
            handleCopyStats={handleCopyStats}
            registerMenuRef={registerMenuRef}
            isRegisterMenuOpen={isRegisterMenuOpen}
            setIsRegisterMenuOpen={setIsRegisterMenuOpen}
            openAddModal={openAddModal}
            setIsBulkModalOpen={setIsBulkModalOpen}
            hideTitle
          />

          <StudentListContentSection
            viewMode={viewMode}
            sortedStudents={sortedStudents}
            selectedIds={selectedIds}
            demoAction={demoAction}
            visitLogs={visitLogs}
            GlassCard={GlassCard}
            handleSelectRow={handleSelectRow}
            setViewingStudent={(student) => {
              setIsDeepLinkedDetailOpen(false);
              setViewingStudent(student);
            }}
            userProfile={userProfile}
            icons={ICONS}
            faithStateStyles={FAITH_STATE_STYLES}
            openAddModal={openAddModal}
            sortKey={sortKey}
            sortOrder={sortOrder}
            onSort={(key) => {
              if (sortKey === key) {
                setSortOrder(sortOrder === "asc" ? "desc" : "asc");
              } else {
                setSortKey(key);
                setSortOrder("asc");
              }
            }}
          />

          <StudentDetailModal
            viewingStudent={viewingStudent}
            detailModalPos={detailModalPos}
            handleDetailMouseDown={handleDetailMouseDown}
            onClose={closeViewingStudent}
            visitLogs={visitLogs}
            attendanceLogs={attendanceLogs}
            icons={ICONS}
            theme={theme}
            faithStateStyles={FAITH_STATE_STYLES}
            onOpenLog={setViewingLog}
            isTeacher={isTeacher}
            onShare={handleShareClick}
            onEdit={openEditModal}
            onDelete={handleDelete}
          />
          <StudentBulkRegistrationModal
            isOpen={isBulkModalOpen}
            onClose={() => setIsBulkModalOpen(false)}
            onAdd={onAdd}
            openAlert={openAlert}
            defaultDepartment={getDefaultDepartment()}
          />
          <StudentEditModal
            isOpen={isModalOpen}
            demoAction={demoAction}
            listType={listType}
            editingStudent={editingStudent}
            modalPos={modalPos}
            onMouseDown={handleMouseDown}
            onClose={() => setIsModalOpen(false)}
            icons={ICONS}
            onSubmit={handleSubmit}
            formData={formData}
            isUploading={isUploading}
            onImageUpload={handleImageUpload}
            setFormData={setFormData}
            allDepartments={allDepartments}
            theme={theme}
          />
        </div>
      </div>

      {/* [NEW] 심방 일지 상세 모달 (In-Place) */}
      <VisitLogModal
        log={viewingLog}
        onClose={() => setViewingLog(null)}
        userProfile={userProfile}
        theme={theme}
        onUpdate={(updatedLog) => {
          if (onUpdateLog) onUpdateLog(updatedLog);
          setViewingLog(updatedLog);
        }}
        onDelete={(id) => {
          if (onDeleteLog) onDeleteLog(id);
          setViewingLog(null);
        }}
      />
    </div>
  );
}
